import classNames from "classnames";
import Table from "components/DesignSystem/Table/V2/Table";
import { CaretDown } from "components/icons/CaretDown";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { Fragment } from "react";
import { flexRender, Row } from "react-table-8.10.7";
import { FinancialClosingDetail } from "store/apis/financialClosing";
import { pluralize } from "utils/pluralize";

type FinancialClosingTableRowProps = {
  row: Row<FinancialClosingDetail>;
  onRowClick: (row: Row<FinancialClosingDetail>) => void;
};

const VersionConnector = ({ hasNext }: { hasNext: boolean }) => (
  <>
    <span className="t-absolute t-left-5 t-top-0 t-h-1/2 t-w-3 t-border-0 t-border-l t-border-b t-border-solid t-border-neutral-20 t-rounded-bl" />
    {hasNext && (
      <span className="t-absolute t-left-5 t-top-1/2 t-bottom-0 t-border-0 t-border-l t-border-solid t-border-neutral-20" />
    )}
  </>
);

const EarlierVersionsRow = ({ row }: { row: Row<FinancialClosingDetail> }) => {
  const { earlier_versions_count = 0, original_sent_at } = row.original;
  const isExpanded = row.getIsExpanded();

  let label = pluralize(
    earlier_versions_count,
    "earlier closing",
    "earlier closings"
  );

  if (original_sent_at) {
    label = `${label} | original sent on: ${dayjs(original_sent_at).format(
      DISPLAY_DATE
    )}`;
  }

  const handleToggle = () => row.toggleExpanded();

  return (
    <Table.Row
      onRowClick={handleToggle}
      className={classNames({ "!t-border-b-0": isExpanded })}
    >
      <Table.Cell colSpan={row.getVisibleCells().length} className="t-relative">
        <VersionConnector hasNext={isExpanded} />
        <div className="t-flex t-items-center t-gap-2 t-pl-7 t-text-body-sm t-text-text-30">
          <span>{label}</span>
          <span
            className={classNames("t-flex t-transition-transform", {
              "t-rotate-180": isExpanded,
            })}
          >
            <CaretDown />
          </span>
        </div>
      </Table.Cell>
    </Table.Row>
  );
};

export const FinancialClosingTableRow = ({
  row,
  onRowClick,
}: FinancialClosingTableRowProps) => {
  const parentRow = row.getParentRow();
  const siblings = parentRow?.subRows ?? [];
  const isChild = row.depth > 0;
  const isLastChild = siblings[siblings.length - 1]?.id === row.id;
  const hasEarlierVersions = !isChild && row.subRows.length > 0;
  const hideBottomBorder = hasEarlierVersions || (isChild && !isLastChild);

  const handleRowClick = () => onRowClick(row);

  return (
    <Fragment>
      <Table.Row
        onRowClick={handleRowClick}
        className={classNames({ "!t-border-b-0": hideBottomBorder })}
      >
        {row.getVisibleCells().map((cell, index) => (
          <Table.Cell
            key={cell.id}
            className={classNames({ "t-relative": index === 0 })}
          >
            {isChild && index === 0 && (
              <VersionConnector hasNext={!isLastChild} />
            )}
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </Table.Cell>
        ))}
      </Table.Row>
      {hasEarlierVersions && <EarlierVersionsRow row={row} />}
    </Fragment>
  );
};
